import { Client, type Room } from '@colyseus/sdk';
import { parseArmor, type ServerMessages } from '@qoobd/rules';
import { audio } from '../audio/engine';
import { game } from './controller';
import { RemoteMatch } from './remote';
import { useGame, type OnlineStatus } from './store';

/**
 * Online 1v1 connection: quick match against anyone, hosting a private room for a friend, or
 * joining a friend by code. The server (Fly.io) runs the match; see packages/game-server.
 */

function serverUrl(): string {
  const env = (import.meta.env as Record<string, string | undefined>).VITE_GAME_SERVER;
  if (env) return env;
  if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') return `ws://${location.hostname}:2567`;
  return 'wss://qoobd-game.fly.dev';
}

let room: Room | null = null;
let remote: RemoteMatch | null = null;

const setOnline = (patch: Partial<OnlineStatus>) => useGame.setState((s) => ({ online: { ...s.online, ...patch } }));

function friendly(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (/not found|invalid|locked|full/i.test(msg)) return "That game isn't available any more. Ask your friend for a new link.";
  return "Couldn't reach the game server. Check your connection and try again.";
}

function wire(r: Room): void {
  room = r;
  const on = <K extends keyof ServerMessages>(type: K, fn: (m: ServerMessages[K]) => void) => r.onMessage(type, fn as (m: unknown) => void);

  on('waiting', (m) => setOnline({ status: 'waiting', code: m.code }));
  on('ready', (m) => {
    remote = new RemoteMatch(m.maze, (action) => r.send('input', { action }), Array.isArray(m.mirrors) ? m.mirrors : []);
    useGame.setState({ rivalArmor: parseArmor(m.rivalArmor) }); // tolerate an older server
    setOnline({ status: 'countdown', startsAt: performance.now() + m.startsInMs, rematchAsked: false, rematchSent: false, opponentAway: false });
  });
  on('state', (m) => {
    const rm = remote;
    if (!rm) return;
    const first = !rm.ready;
    rm.apply(m.t, m.view, m.events);
    if (first) {
      game.startOnline(rm);
      setOnline({ status: 'playing' });
    }
  });
  on('rejected', (m) => game.rejected(m.reason));
  on('over', (m) => {
    game.finishOnline(m);
    setOnline({ status: 'over' });
  });
  on('opponentAway', () => setOnline({ opponentAway: true }));
  on('opponentBack', () => setOnline({ opponentAway: false }));
  on('rematchAsked', () => setOnline({ rematchAsked: true }));
  r.onLeave(() => {
    if (room !== r) return;
    room = null;
    const s = useGame.getState();
    if (s.online.status !== 'over' && s.online.status !== 'idle') setOnline({ status: 'error', error: 'The connection to the game server was lost.' });
  });
}

async function connect(how: (c: Client) => Promise<Room>): Promise<void> {
  await leaveOnline();
  audio.unlock();
  setOnline({ status: 'connecting', code: null, error: null, opponentAway: false, rematchAsked: false, rematchSent: false });
  try {
    wire(await how(new Client(serverUrl())));
  } catch (e) {
    setOnline({ status: 'error', error: friendly(e) });
  }
}

/** Play the next person who's looking for a match. */
const armor = () => ({ armor: useGame.getState().armor });
export const quickMatch = () => connect((c) => c.joinOrCreate('duel', armor()));
/** Host a private game; the waiting screen shows a code and link to send a friend. */
export const hostFriend = () => connect((c) => c.create('duel', { private: true, ...armor() }));
/** Join a friend's private game by its code. */
export const joinFriend = (code: string) => connect((c) => c.joinById(code.trim(), armor()));

export function askRematch(): void {
  room?.send('rematch', {});
  setOnline({ rematchSent: true });
}

/** Leave the online game (forfeits a match in progress) and return to idle. */
export async function leaveOnline(): Promise<void> {
  const r = room;
  room = null;
  remote = null;
  setOnline({ status: 'idle', code: null, error: null, opponentAway: false, rematchAsked: false, rematchSent: false });
  if (r) {
    try {
      await r.leave(true);
    } catch {
      /* already gone */
    }
  }
}

export function friendLink(code: string): string {
  return `${location.origin}/?join=${encodeURIComponent(code)}`;
}

export function joinCodeFromLocation(): string | null {
  try {
    const v = new URLSearchParams(location.search).get('join');
    return v && /^[\w-]{4,40}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}
